/**
 * Phase F3 — Provider Tax Center
 * -------------------------------
 * Surfaces Phase 8 Tax Reporting data for the provider role.
 *
 * Endpoints used:
 *   GET /api/provider/tax-center                — yearly totals + reporting records + distribution history
 *   GET /api/provider/tax-center?tax_year=YYYY  — filtered to one year
 *
 * Pure UI. No backend logic modified.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileSpreadsheet, Calendar, DollarSign, FileText, Clock, CheckCircle,
  AlertTriangle, TrendingUp,
} from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, boxShadow: theme.colors.shadows.sm, padding: 20,
};

const STATUS_META = {
  PENDING: { color: theme.colors.status.warning, Icon: Clock },
  READY: { color: theme.colors.status.success, Icon: CheckCircle },
  ARCHIVED: { color: theme.colors.text.muted, Icon: FileText },
  ERROR: { color: theme.colors.status.error, Icon: AlertTriangle },
};

function fmtMoney(n, cur = 'USD') {
  return `${(Number(n) || 0).toFixed(2)} ${cur}`;
}

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || { color: theme.colors.text.muted, Icon: FileText };
  const Icon = meta.Icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 999,
      background: `${meta.color}1A`, color: meta.color,
      fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
    }}>
      <Icon size={12} /> {status || '—'}
    </span>
  );
};

export default function ProviderTaxCenter() {
  const [data, setData] = useState({ yearly_summaries: [], reporting_records: [], distribution_history: [] });
  const [loading, setLoading] = useState(true);
  const [yearFilter, setYearFilter] = useState('');

  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const url = yearFilter ? `${API}/provider/tax-center?tax_year=${yearFilter}` : `${API}/provider/tax-center`;
        const res = await axios.get(url, auth);
        if (cancelled) return;
        setData({
          yearly_summaries: res.data?.yearly_summaries || [],
          reporting_records: res.data?.reporting_records || [],
          distribution_history: res.data?.distribution_history || [],
        });
      } catch (e) {
        if (!cancelled) toast.error('Failed to load tax center');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [auth, yearFilter]);

  const availableYears = useMemo(() => {
    const ys = new Set(data.yearly_summaries.map((y) => y.tax_year));
    return Array.from(ys).sort((a, b) => b - a);
  }, [data.yearly_summaries]);

  const lifetimeFees = useMemo(() => {
    return data.yearly_summaries.reduce((acc, y) => acc + Number(y.total_distribution || y.gross_total || 0), 0);
  }, [data.yearly_summaries]);

  return (
    <div data-testid="provider-tax-center-page" style={{ padding: 24, maxWidth: 1300, margin: '0 auto' }}>
      <div style={{ marginBottom: 18, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{
            fontSize: 28, fontWeight: 800, color: theme.colors.text.primary, margin: 0,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <FileSpreadsheet size={26} color={theme.colors.primary} />
            Provider Tax Center
          </h1>
          <p style={{ color: theme.colors.text.secondary, marginTop: 4 }}>
            Yearly totals, fee earnings, reporting status across all your engagements.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 12, color: theme.colors.text.muted, fontWeight: 600 }}>Tax year</label>
          <select
            data-testid="provider-tax-year-filter"
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            style={{
              padding: '8px 12px', borderRadius: theme.radius.sm,
              border: `1px solid ${theme.colors.borders}`, background: theme.colors.bg.card,
              color: theme.colors.text.primary, fontSize: 13,
            }}
          >
            <option value="">All years</option>
            {availableYears.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* Lifetime card */}
      <div data-testid="provider-tax-lifetime-card" style={{
        ...cardStyle, marginBottom: 16, display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 42, height: 42, borderRadius: theme.radius.sm,
            background: `${theme.colors.primary}15`, color: theme.colors.primary,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <TrendingUp size={20} />
          </div>
          <div>
            <div style={{ fontSize: 12, color: theme.colors.text.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Lifetime Fee Earnings
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: theme.colors.text.primary, marginTop: 2 }}>
              {fmtMoney(lifetimeFees)}
            </div>
          </div>
        </div>
        <div style={{ fontSize: 12, color: theme.colors.text.muted }}>
          {data.yearly_summaries.length} year(s) · {data.reporting_records.length} reporting record(s)
        </div>
      </div>

      {/* Yearly totals */}
      <div style={{ ...cardStyle, marginBottom: 16 }} data-testid="provider-yearly-totals-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Calendar size={16} color={theme.colors.primary} />
          <div style={{ fontWeight: 700, color: theme.colors.text.primary }}>Yearly Totals</div>
        </div>
        {loading ? (
          <div style={{ color: theme.colors.text.muted, padding: 12 }} data-testid="provider-tax-loading">Loading…</div>
        ) : data.yearly_summaries.length === 0 ? (
          <div style={{ color: theme.colors.text.secondary, padding: 8 }} data-testid="provider-yearly-empty">
            No yearly summary data yet.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            {data.yearly_summaries.map((y) => (
              <div
                key={`${y.tax_year}-${y.role}`}
                data-testid={`provider-year-card-${y.tax_year}`}
                style={{
                  padding: 16, background: theme.colors.bg.secondary,
                  borderRadius: theme.radius.md, border: `1px solid ${theme.colors.borders}`,
                }}
              >
                <div style={{ fontSize: 11, color: theme.colors.text.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Tax Year
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: theme.colors.text.primary, marginBottom: 8 }}>
                  {y.tax_year}
                </div>
                <div style={{ display: 'grid', gap: 6, fontSize: 13 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: theme.colors.text.secondary }}>Fee earnings</span>
                    <span style={{ fontWeight: 700, color: theme.colors.text.primary }}>
                      {fmtMoney(y.total_distribution || y.gross_total, y.currency)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: theme.colors.text.secondary }}>Taxable total</span>
                    <span style={{ fontWeight: 700, color: theme.colors.text.primary }}>
                      {fmtMoney(y.taxable_total || y.reportable_total, y.currency)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: theme.colors.text.secondary }}>Distributions</span>
                    <span style={{ fontWeight: 700, color: theme.colors.text.primary }}>
                      {(y.distribution_ids || []).length}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reporting status */}
      <div style={{ ...cardStyle, marginBottom: 16 }} data-testid="provider-reporting-records-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <FileText size={16} color={theme.colors.primary} />
          <div style={{ fontWeight: 700, color: theme.colors.text.primary }}>Reporting Status</div>
        </div>
        {data.reporting_records.length === 0 ? (
          <div style={{ color: theme.colors.text.secondary, padding: 8 }} data-testid="provider-records-empty">
            No reporting records yet.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: theme.colors.bg.secondary }}>
              <tr>
                {['Tax Year', 'Form', 'Gross', 'Taxable', 'Status', 'Updated'].map((h) => (
                  <th key={h} style={{
                    textAlign: 'left', padding: '10px 12px',
                    fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5,
                    color: theme.colors.text.muted, fontWeight: 700,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.reporting_records.map((r) => (
                <tr key={r.id} style={{ borderTop: `1px solid ${theme.colors.borders}` }}>
                  <td style={{ padding: '10px 12px', fontWeight: 700, color: theme.colors.text.primary }}>{r.tax_year}</td>
                  <td style={{ padding: '10px 12px', color: theme.colors.text.secondary }}>{r.form_type || '1099'}</td>
                  <td style={{ padding: '10px 12px' }}>{fmtMoney(r.gross_total, r.currency)}</td>
                  <td style={{ padding: '10px 12px' }}>{fmtMoney(r.taxable_total, r.currency)}</td>
                  <td style={{ padding: '10px 12px' }}><StatusPill status={r.status} /></td>
                  <td style={{ padding: '10px 12px', color: theme.colors.text.secondary }}>
                    {r.updated_at ? new Date(r.updated_at).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Distribution history */}
      <div style={cardStyle} data-testid="provider-tax-history-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <DollarSign size={16} color={theme.colors.primary} />
          <div style={{ fontWeight: 700, color: theme.colors.text.primary }}>Distribution History</div>
        </div>
        {data.distribution_history.length === 0 ? (
          <div style={{ color: theme.colors.text.secondary, padding: 8 }} data-testid="provider-tax-history-empty">
            No distributions in your tax history yet.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: theme.colors.bg.secondary }}>
              <tr>
                {['Portfolio', 'Model', 'Total', 'Status', 'Date'].map((h) => (
                  <th key={h} style={{
                    textAlign: 'left', padding: '10px 12px',
                    fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5,
                    color: theme.colors.text.muted, fontWeight: 700,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.distribution_history.map((d) => (
                <tr key={d.id} style={{ borderTop: `1px solid ${theme.colors.borders}` }}>
                  <td style={{ padding: '10px 12px', fontWeight: 600, color: theme.colors.text.primary }}>
                    {d.portfolio_name || d.portfolio_id}
                  </td>
                  <td style={{ padding: '10px 12px', color: theme.colors.text.secondary }}>{d.distribution_model || '—'}</td>
                  <td style={{ padding: '10px 12px' }}>{fmtMoney(d.total_amount || d.distribution_amount, d.currency)}</td>
                  <td style={{ padding: '10px 12px' }}><StatusPill status={d.status} /></td>
                  <td style={{ padding: '10px 12px', color: theme.colors.text.secondary }}>
                    {d.paid_at ? new Date(d.paid_at).toLocaleDateString() :
                     d.generated_at ? new Date(d.generated_at).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
